import { useEffect, useMemo, useState } from "react";
import {
  subscribeStaffHolidayBalance,
  subscribeStaffHolidayProfile,
} from "../services/staffHolidayFirebase";

const normalizeEmail = (email = "") =>
  String(email || "")
    .trim()
    .toLowerCase();

const normalizeDepartments = (departments) => {
  if (!departments) return [];

  const departmentArray = Array.isArray(departments)
    ? departments
    : [departments];

  return departmentArray
    .map((department) => {
      if (typeof department === "string") {
        const value = department.trim();

        return value
          ? {
              id: value,
              name: value,
            }
          : null;
      }

      if (department && typeof department === "object") {
        const id = String(
          department.id ||
            department.departmentId ||
            department.value ||
            department.name ||
            department.label ||
            "",
        ).trim();

        const name = String(
          department.name ||
            department.departmentName ||
            department.label ||
            id,
        ).trim();

        return id
          ? {
              id,
              name: name || id,
            }
          : null;
      }

      return null;
    })
    .filter(Boolean);
};

const normalizePeople = (value) => {
  if (!value) return [];

  const peopleArray = Array.isArray(value) ? value : [value];

  return peopleArray
    .map((person) => {
      if (typeof person === "string") {
        const email = normalizeEmail(person);

        return email
          ? {
              name: email,
              email,
            }
          : null;
      }

      if (person && typeof person === "object") {
        const email = normalizeEmail(
          person.email || person.value || person.id || "",
        );

        const name = String(
          person.name || person.label || person.displayName || email,
        ).trim();

        return email || name
          ? {
              name: name || email,
              email,
            }
          : null;
      }

      return null;
    })
    .filter(Boolean);
};

const normalizeEmailList = (value) => {
  if (!value) return [];

  const emailArray = Array.isArray(value) ? value : [value];

  return emailArray.map(normalizeEmail).filter(Boolean);
};

const normalizeHolidayProfile = (data = null, staffEmail = "") => {
  if (!data) return null;

  const departments = normalizeDepartments(data.departments);
  const primaryDepartment = departments[0];

  const holidayManagers = normalizePeople(
    data.holidayManagers ||
      data.holidayManager ||
      data.lineManagers ||
      data.lineManager,
  );

  const holidayManagerEmailsFromManagers = holidayManagers
    .map((manager) => manager.email)
    .filter(Boolean);

  const holidayManagerEmails = [
    ...new Set([
      ...normalizeEmailList(
        data.holidayManagerEmails ||
          data.holidayManagerEmail ||
          data.lineManagerEmails ||
          data.lineManagerEmail,
      ),
      ...holidayManagerEmailsFromManagers,
    ]),
  ];

  return {
    ...data,

    staffEmail: normalizeEmail(data.staffEmail || data.email || staffEmail),
    email: normalizeEmail(data.email || data.staffEmail || staffEmail),

    departments,

    departmentId: String(
      data.departmentId || primaryDepartment?.id || "",
    ).trim(),

    departmentName: String(
      data.departmentName || primaryDepartment?.name || "",
    ).trim(),

    holidayManagers,
    holidayManagerEmails,

    // Keep old names temporarily so older components do not break
    lineManagers: holidayManagers,
    lineManagerEmails: holidayManagerEmails,
  };
};

export const useStaffHolidayProfile = (staffEmail, year) => {
  const safeStaffEmail = useMemo(
    () => normalizeEmail(staffEmail),
    [staffEmail],
  );

  const [profile, setProfile] = useState(null);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(Boolean(safeStaffEmail));
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!safeStaffEmail) {
      setProfile(null);
      setBalance(null);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    setError(null);

    return subscribeStaffHolidayProfile({
      staffEmail: safeStaffEmail,
      onData: (data) => {
        const normalizedProfile = normalizeHolidayProfile(data, safeStaffEmail);

        // console.log("[useStaffHolidayProfile] profile", {
        //   staffEmail: safeStaffEmail,
        //   rawDepartments: data?.departments,
        //   normalizedDepartments: normalizedProfile?.departments,
        //   rawHolidayManagers: data?.holidayManagers || data?.lineManagers,
        //   normalizedHolidayManagers: normalizedProfile?.holidayManagers,
        //   departmentId: normalizedProfile?.departmentId,
        //   departmentName: normalizedProfile?.departmentName,
        // });

        setProfile(normalizedProfile);
        setLoading(false);
      },
      onError: (err) => {
        // console.error("[useStaffHolidayProfile] profile error", err);
        setError(err);
        setLoading(false);
      },
    });
  }, [safeStaffEmail]);

  useEffect(() => {
    if (!safeStaffEmail || !year) {
      setBalance(null);
      return undefined;
    }

    setError(null);

    return subscribeStaffHolidayBalance({
      staffEmail: safeStaffEmail,
      year,
      onData: (data) => {
        setBalance(data);
      },
      onError: (err) => {
        // console.error("[useStaffHolidayProfile] balance error", err);
        setError(err);
      },
    });
  }, [safeStaffEmail, year]);

  return {
    profile,
    balance,
    loading,
    error,
  };
};
